import { Controller } from "stimulus"

// Presents the dismissal-feedback modals. Opening is driven by the streak
// controller on the same element; this one owns the once-per-session gate,
// reason selection, and which outcome the member lands on.
//
// Capture is not here: the submit button carries event-tracker attributes, so
// the response travels the same path as every other client event in the app.
const SEEN_COOKIE = "next_steps_feedback_seen"
const SELECTED = "next-steps-feedback__reason--selected"
const THANKS_DURATION = 3500

export default class extends Controller {
  static targets = ["dialog", "exploreDialog", "reason", "reasonValue", "freeText", "submit", "thanksToast", "exploreCta", "frame"]

  connect() {
    // A cached page restores whatever was on screen when the member left, which
    // would bring back an open modal with no streak behind it.
    this.onBeforeCache = () => this.resetForCache()
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    this.clearThanksTimer()
    document.body.style.overflow = ""
  }

  open() {
    if (this.alreadySeen()) return

    this.resetForm()
    this.reveal(this.dialogTarget)
    this.markSeen()

    const first = this.reasonTargets[0]
    if (first) first.focus()
  }

  dismiss() {
    this.conceal(this.dialogTarget)
    this.restoreFocus()
  }

  // Runs after event-tracker#track on the same click, so the values it reads are
  // still in the DOM when it reads them.
  submit() {
    const chosen = this.selectedReason()
    if (!chosen) return

    this.resetForm()
    this.conceal(this.dialogTarget)

    if (chosen.dataset.outcome === "explore") {
      this.reveal(this.exploreDialogTarget)
      if (this.hasExploreCtaTarget) this.exploreCtaTarget.focus()
      return
    }

    this.showThanks()
    this.restoreFocus()
  }

  closeExplore() {
    this.conceal(this.exploreDialogTarget)
    this.restoreFocus()
  }

  selectReason(event) {
    const chosen = event.currentTarget

    this.reasonTargets.forEach((button) => {
      button.classList.toggle(SELECTED, button === chosen)
      button.setAttribute("aria-pressed", String(button === chosen))
    })
    this.reasonValueTarget.value = chosen.dataset.reason
    this.submitTarget.disabled = false
  }

  closeOnEsc(event) {
    if (event.key !== "Escape") return

    if (this.hasExploreDialogTarget && this.open_(this.exploreDialogTarget)) return this.closeExplore()
    if (this.hasDialogTarget && this.open_(this.dialogTarget)) this.dismiss()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  showThanks() {
    this.clearThanksTimer()
    this.thanksToastTarget.hidden = false
    this.thanksTimer = setTimeout(() => {
      this.thanksToastTarget.hidden = true
    }, THANKS_DURATION)
  }

  clearThanksTimer() {
    if (this.thanksTimer) clearTimeout(this.thanksTimer)
    this.thanksTimer = null
  }

  resetForCache() {
    if (this.hasDialogTarget) this.conceal(this.dialogTarget)
    if (this.hasExploreDialogTarget) this.conceal(this.exploreDialogTarget)
    if (this.hasThanksToastTarget) this.thanksToastTarget.hidden = true
    this.resetForm()
    this.clearThanksTimer()
  }

  // Session replay re-serializes whatever is still in the DOM, and Turbo puts the
  // same DOM in its back-button snapshot.
  resetForm() {
    if (!this.hasSubmitTarget) return

    this.reasonTargets.forEach((button) => {
      button.classList.remove(SELECTED)
      button.setAttribute("aria-pressed", "false")
    })
    this.reasonValueTarget.value = ""
    this.freeTextTarget.value = ""
    this.submitTarget.disabled = true
  }

  // The shared overlay hides itself with an inline display, not the hidden
  // attribute, so visibility has to be read from the same place it is written.
  reveal(dialog) {
    dialog.style.display = "flex"
    document.body.style.overflow = "hidden"
  }

  conceal(dialog) {
    dialog.style.display = "none"
    document.body.style.overflow = ""
  }

  open_(dialog) {
    return dialog.style.display !== "none"
  }

  // The card that triggered the streak is gone from the DOM by the time the
  // modal opens, so focus returns to the section frame rather than the control
  // the member last touched.
  restoreFocus() {
    if (!this.hasFrameTarget) return

    this.frameTarget.setAttribute("tabindex", "-1")
    this.frameTarget.focus()
  }

  selectedReason() {
    return this.reasonTargets.find((button) => button.classList.contains(SELECTED)) || null
  }

  alreadySeen() {
    return document.cookie.split("; ").some((c) => c.startsWith(`${SEEN_COOKIE}=`))
  }

  // No max-age on purpose: the cookie dies with the browser session, and
  // SessionsController drops it on sign out.
  markSeen() {
    document.cookie = `${SEEN_COOKIE}=true; path=/; SameSite=Lax`
  }
}
